import { useState, useEffect } from 'react'
import { Select, Button, Modal, Form, Input, message } from 'antd'
import { PlusOutlined, FolderOpenOutlined } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchProjects, createProject, setCurrentProject, setCurrentUserProject } from '../store/slices/projectSlice'

const { Option } = Select

interface CreateProjectFormData {
  name: string
  description?: string
}

const ProjectToggle = () => {
  const dispatch = useAppDispatch()
  const { currentProject, projects, isLoading } = useAppSelector((state) => state.project)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [form] = Form.useForm<CreateProjectFormData>()
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (projects.length === 0) {
      dispatch(fetchProjects())
    }
  }, [dispatch, projects.length])

  const handleProjectChange = async (projectId: string) => {
    const project = projects.find(p => p.id === projectId)
    if (project) {
      // Update local state immediately
      dispatch(setCurrentProject(project))
      
      // Also persist to backend (fire and forget)
      try {
        await dispatch(setCurrentUserProject(projectId)).unwrap()
      } catch (error) {
        // Silent fail - localStorage persistence is enough for now
        console.warn('Failed to persist project selection to backend:', error)
      }
    }
  }

  const handleCreateProject = async (values: CreateProjectFormData) => {
    try {
      setCreating(true)
      await dispatch(createProject(values)).unwrap()
      
      message.success('Project created successfully')
      setShowCreateModal(false)
      form.resetFields()
    } catch (error: any) {
      message.error(error || 'Failed to create project')
    } finally {
      setCreating(false)
    }
  }

  const handleCancelCreate = () => {
    setShowCreateModal(false)
    form.resetFields()
  }

  return (
    <>
      <div className="flex items-center space-x-2">
        <FolderOpenOutlined className="text-gray-500" />
        <Select
          value={currentProject?.id}
          onChange={handleProjectChange}
          loading={isLoading}
          placeholder="Select a project"
          style={{ minWidth: 200 }}
          popupRender={(menu) => (
            <div>
              {menu}
              <div className="border-t border-gray-200 p-2">
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => setShowCreateModal(true)}
                  className="w-full text-left"
                >
                  Create New Project
                </Button>
              </div>
            </div>
          )}
        >
          {projects.map((project) => (
            <Option key={project.id} value={project.id}>
              <div className="flex items-center justify-between">
                <span>{project.name}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  project.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {project.status}
                </span>
              </div>
            </Option>
          ))}
        </Select>
      </div>

      <Modal
        title="Create New Project"
        open={showCreateModal}
        onCancel={handleCancelCreate}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateProject}
          className="mt-4"
        >
          <Form.Item
            name="name"
            label="Project Name"
            rules={[
              { required: true, message: 'Please enter a project name' },
              { min: 2, message: 'Project name must be at least 2 characters' }
            ]}
          >
            <Input placeholder="Enter project name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Enter project description (optional)"
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <div className="flex justify-end space-x-2">
              <Button onClick={handleCancelCreate}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={creating}
              >
                Create Project
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default ProjectToggle